import React, { useState, useEffect, useMemo } from 'react';
import PerinHealthGrid from '../../components/PerinHealthGrid';
import { setSelectAll, setSelectedItem, setSelectedRows } from '../../components/PerinHealthGridSlice';
import { resetFilterState } from '../../slicers/FilterSlice';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import useAxios from '../../services/AxiosConfig';
import FilterManagementView from '../../components/FilterManagementView';
import { closeModal } from '../../components/PerinHealthModelSlice';
import managementStyles from '../ManagementPages.module.css';
import { showAlert } from '../../components/PerinHealthAlertSlice';
import { Modal, Button, Form } from 'react-bootstrap';
import { formatDateString } from '../../utils/dateUtils';

const TabletRegistry = () => {
    const dispatch = useDispatch();
    const axiosConfig = useSelector((state) => state.axiosConfig);
    const axios = useAxios(axiosConfig.envURL);
    const userDateFormat = useSelector((state) => state.userInfo.dateFormat);
    const tabletSlice = useSelector((state)=>state.tabletManagement);
    const [patientLocation, setPatientLocation] = useState([]);
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [selectedPatientLocation, setSelectedPatientLocation] = useState('');
    const [selectedTabletItem, setSelectedTabletItem] = useState(null);
    const [tabletData, setTabletData] = useState([]);

    useEffect(() => {
        dispatch(resetFilterState());
        dispatch(setSelectedRows([]));
        dispatch(setSelectedItem({}));
        getTabletData();
        getPatientLocation();
    }, []);

    const navigate = useNavigate();
    const user = useSelector((state) => state.userInfo);
    const orgId = user.groups[0]?.organizationId;

    const headers = [
        { key: 'serialNumber', label: 'Serial Number'},
        { key: 'macAddress', label: "Mac Address"},
        { key: 'floor', label: "Floor"},
        { key: 'room', label: "Room"},
        { key: 'bed', label: "Bed"},
        { key: 'patientId', label: "Patient ID"},
        { key: 'formattedDate', label: "Date Assigned"},

    ];


    const getTabletData = () => {
        axios.get(`tablet/list/group/${tabletSlice.registryData.groupId}`)
        .then((responseData) => {
            if (Array.isArray(responseData.data.data)) {
                const formattedData = responseData.data.data.map(item => {
                    const formattedDate = formatDateString(item.dateAssigned, userDateFormat);
                    return {
                        ...item,
                        formattedDate: formattedDate,
                    }
                });
                setTabletData(formattedData);
            } else {
                console.error('Invalid data format:');
            }
        })
        .catch((err) => {
            console.log(err);
        });
    };


    const getPatientLocation = async () => {
    };

    const getDropdownOptions = (item) => {
        const options = [];
        if (item.isAssigned) {
            options.push({label:"Unassign Tablet", type:"unassign"});
        }
        options.push({label:"Deactivate Tablet", type:"deactivate"});
        return options;
    };

    const assignTablet = (item) => {
        setSelectedTabletItem(item);
        setShowAssignModal(true);
    }

    const unassignTablet = (item) => {
        axios.post(`tablet/unassign/${item.id}`)
        .then((responseData) => {
            dispatch(
                showAlert({
                    header: "Success",
                    message: "Tablet unassigned successfully",
                    type: 'success',
                })
            );
            getTabletData();
        })
        .catch((err) => {
            dispatch(
                showAlert({
                    header: "Error",
                    message: "Error unassigning tablet",
                    type: 'danger',
                })
            );
        });
    }

    const handleConfirmAssign = () => {
        if (!selectedPatientLocation) {
            dispatch(
                showAlert({
                    header: "Error",
                    message: "Please select a patient location",
                    type: 'danger',
                })
            );
            return;
        }
        const selectedLocation = availablePatientLocations.find(loc => 
            loc.patient_id === selectedPatientLocation || 
            loc.encounter_id === selectedPatientLocation
        );

        if (!selectedLocation) {
            dispatch(
                showAlert({
                    header: "Error",
                    message: "Selected patient location not found",
                    type: 'danger',
                })
            );
            return;
        }

        axios.post(`tablet/assign/${selectedTabletItem.id}`, {
            patientId: selectedLocation.perin_user_id,
            floor: selectedLocation.floor,
            room: selectedLocation.room,
            bed: selectedLocation.bed,
        })
        .then((responseData) => {
            dispatch(
                showAlert({
                    header: "Success",
                    message: "Tablet assigned successfully",
                    type: 'success',
                })
            );
            getTabletData();
        })
        .catch((err) => {
            dispatch(
                showAlert({
                    header: "Error",
                    message: "Error assigning tablet",
                    type: 'danger',
                })
            );
        });

        // Close modal and reset state
        setShowAssignModal(false);
        setSelectedPatientLocation('');
        setSelectedTabletItem(null);
    }

    const handleCloseAssignModal = () => {
        setShowAssignModal(false);
        setSelectedPatientLocation('');
        setSelectedTabletItem(null);
    }

    const handleDropdownClick = ({type, item}) => {
        if (type === "assign") {
            assignTablet(item)
        } else if (type === "unassign") {
            unassignTablet(item)
        } else if (type === "deactivate") {
            deactivateTablet(item.id)
        } else {
            return;
        }
    };

    const deactivateTablet = (id) => {
        axios.delete(`tablet/${id}`)
        .then((responseData) => {
            dispatch(
                showAlert({
                    header: "Success",
                    message: "Tablet deactivated successfully",
                    type: 'success',
                })
            );
            getTabletData();
        })
        .catch((err) => {
            dispatch(
                showAlert({
                    header: "Error",
                    message: "Error deactivating tablet",
                    type: 'danger',
                })
            );
        });
    }

    // Filter patient locations to exclude already assigned patients
    const availablePatientLocations = useMemo(() => {
        // Get all assigned patient IDs from tabletData
        const assignedPatientIds = new Set(
            tabletData
                .filter(tablet => tablet.isAssigned && tablet.patientId)
                .map(tablet => tablet.patientId)
        );

        // Filter out patients that are already assigned
        return patientLocation.filter(location => {
            // Check if this patient's perin_user_id is already assigned
            return !assignedPatientIds.has(location.perin_user_id);
        });
    }, [tabletData, patientLocation]);

    return (
        <div className={managementStyles.MiddleContent}>
            <FilterManagementView
                isOrganization={false}
                isGroup={false}
                isSubGroup={false}
                isTeam={false}
                showAddButton={false}
                showDeleteButton={false}
                modelHeader='Delete Tablet?' 
            />
            <div className={managementStyles.GridContainer}>
                <PerinHealthGrid
                    dataList={tabletData}
                    headers={headers}
                    showCheckbox={true}
                    showEditIcon={true}
                    showHeaderFilter={true}
                    gridHeight="100%"
                    moreEditOptions={getDropdownOptions}
                    onRowClick={handleDropdownClick}
                />
            </div>

            {/* Assign Tablet Modal */}
            <Modal show={showAssignModal} onHide={handleCloseAssignModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Assign Tablet</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Select Patient Location</Form.Label>
                            <Form.Select
                                value={selectedPatientLocation}
                                onChange={(e) => setSelectedPatientLocation(e.target.value)}
                            >
                                <option value="">Select a patient location...</option>
                                {availablePatientLocations.length > 0 ? (
                                    availablePatientLocations.map((location) => (
                                        <option 
                                            key={location.encounter_id || location.patient_id} 
                                            value={location.encounter_id || location.patient_id}
                                        >
                                            {location.name || 'Unknown'} - {location.location || ''} 
                                            {location.room && ` - Room ${location.room}`}
                                            {location.bed && ` - Bed ${location.bed}`}
                                        </option>
                                    ))
                                ) : (
                                    <option value="" disabled>No available patient locations</option>
                                )}
                            </Form.Select>
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseAssignModal}>
                        Cancel
                    </Button>
                    <Button 
                        variant="primary" 
                        onClick={handleConfirmAssign}
                        disabled={!selectedPatientLocation}
                        style={{ backgroundColor: '#1D5769', border: 'none' }}
                    >
                        Confirm
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default TabletRegistry;
